// ... (imports)
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProject } from '../services/api';
import { useLanguage } from '../hooks/useLanguage';
import Navbar from '../components/Navbar';
import Footer from '../components/sections/Footer';
import Button from '../components/ui/Button';
import { ArrowLeft, MapPin, Calendar, User, CheckCircle, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';

const ProjectDetails = () => {
    const { id } = useParams();
    const { language } = useLanguage();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // If getProject API is not implemented on backend for single item public access, 
                // we might need to fetch all and filter, BUT backend route GET /:id IS implemented in projects.js
                // However, I need to check if correct route is exposed. 
                // projects.js doesn't seem to have GET /:id ... DOES IT? 
                // Let me check projects.js content again. 
                // It HAS router.delete('/:id') but GET is router.get('/'). 
                // Wait, I missed checking for GET /:id in projects.js! 
                // I will add GET /:id to backend if missing.
                const data = await getProject(id);
                setProject(data);
            } catch (error) {
                console.error("Failed to load project", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
        window.scrollTo(0, 0);
    }, [id]);

    if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-500"></div></div>;
    if (!project) return <div className="min-h-screen flex items-center justify-center">Project not found</div>;

    // Helper to get localized string
    const getLocalized = (field) => {
        if (typeof field === 'object' && field !== null) {
            return field[language] || field['en'] || '';
        }
        return field;
    };

    // Images array (fallback to single image if array empty)
    const images = project.images && project.images.length > 0 ? project.images : [project.image];

    const nextImage = () => setActiveImageIndex((prev) => (prev + 1) % images.length);
    const prevImage = () => setActiveImageIndex((prev) => (prev - 1 + images.length) % images.length);

    return (
        <div className="bg-white min-h-screen">
            <Navbar forceSolid={true} />

            <div className="pt-24 pb-12 container mx-auto px-4">
                <Link to="/" className="inline-flex items-center text-slate-500 hover:text-gold-500 mb-6 transition-colors font-medium">
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    {language === 'sr' ? 'Nazad na početnu' : 'Back to Home'}
                </Link>

                {/* Header */}
                <div className="mb-12">
                    <span className="text-gold-600 font-bold tracking-wider uppercase text-sm mb-2 block">
                        {getLocalized(project.category)}
                    </span>
                    <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                        {getLocalized(project.title)}
                    </h1>
                    <div className="flex flex-wrap gap-6 text-slate-600 text-sm md:text-base border-y border-slate-100 py-6">
                        <div className="flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-gold-500" />
                            <span>{getLocalized(project.location)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-gold-500" />
                            <span>{project.year}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <User className="w-5 h-5 text-gold-500" />
                            <span>{project.client}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                            <span className="font-medium text-green-700">{getLocalized(project.status)}</span>
                        </div>
                    </div>
                </div>

                <div className="grid lg:grid-cols-3 gap-12">
                    {/* Main Gallery */}
                    <div className="lg:col-span-2">
                        <div className="relative aspect-[3/4] md:aspect-video bg-slate-900 rounded-xl overflow-hidden mb-4 group">
                            {/* Blurred background for fill */}
                            <img
                                src={images[activeImageIndex]}
                                alt=""
                                className="absolute inset-0 w-full h-full object-cover opacity-30 blur-2xl scale-110"
                            />
                            {/* Main image - contained with padding to ensure it's smaller than width */}
                            <img
                                src={images[activeImageIndex]}
                                alt={getLocalized(project.title)}
                                className="relative w-full h-full object-contain z-10 p-2 md:p-0"
                            />

                            {/* Controls */}
                            {images.length > 1 && (
                                <>
                                    <button onClick={prevImage} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/30 backdrop-blur-md text-white p-2 rounded-full transition-all z-20">
                                        <ChevronLeft className="w-6 h-6" />
                                    </button>
                                    <button onClick={nextImage} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/30 backdrop-blur-md text-white p-2 rounded-full transition-all z-20">
                                        <ChevronRight className="w-6 h-6" />
                                    </button>
                                </>
                            )}

                            <button onClick={() => setIsLightboxOpen(true)} className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-20">
                                <Maximize2 className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Thumbnails */}
                        {images.length > 1 && (
                            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                                {images.map((img, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setActiveImageIndex(idx)}
                                        className={`relative w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${activeImageIndex === idx ? 'border-gold-500 ring-2 ring-gold-200' : 'border-transparent opacity-70 hover:opacity-100'}`}
                                    >
                                        <img src={img} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Details Column */}
                    <div className="lg:col-span-1">
                        <div className="bg-slate-50 p-8 rounded-xl sticky top-24">
                            <h3 className="text-xl font-bold text-slate-900 mb-4 border-b pb-4">
                                {language === 'sr' ? 'O Projektu' : 'About Project'}
                            </h3>
                            <p className="text-slate-600 leading-relaxed whitespace-pre-line text-lg">
                                {getLocalized(project.description)}
                            </p>

                            <div className="mt-8">
                                <Button className="w-full" onClick={() => window.location.href = 'mailto:info@nopa.rs'}>
                                    {language === 'sr' ? 'Kontaktirajte nas' : 'Contact Us'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Lightbox Modal */}
            {isLightboxOpen && (
                <div className="fixed inset-0 z-[2000] bg-black/95 flex items-center justify-center p-4">
                    <button onClick={() => setIsLightboxOpen(false)} className="absolute top-6 right-6 text-white hover:text-gold-500 z-50">
                        <span className="text-4xl">&times;</span>
                    </button>

                    <button onClick={prevImage} className="absolute left-4 text-white hover:text-gold-500 z-50"><ChevronLeft className="w-10 h-10" /></button>

                    <img
                        src={images[activeImageIndex]}
                        alt=""
                        className="max-w-full max-h-[90vh] object-contain"
                    />

                    <button onClick={nextImage} className="absolute right-4 text-white hover:text-gold-500 z-50"><ChevronRight className="w-10 h-10" /></button>

                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80">
                        {activeImageIndex + 1} / {images.length}
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default ProjectDetails;
